export type Paging = {
  resultsPerPage: number;
  page: number;
};

export type Sorting = {
  key: string;
  order: SortOrder;
};

export type SortOrder = 'ASC' | 'DESC';

export type CsvResponse = {
  csvUrl: string;
  filename: string;
};

export type StreamingEntry = {
  date: Date;
  value: number;
};

export type StreamsByRegionRaw = {
  [region: string]: {
    value: number;
    date: string;
  }[];
};

export type StreamsByRegion = {
  [region: string]: StreamingEntry[];
};

export type AggregateStreams = {
  percentages: { [key: string]: number };
  totalCount: number;
};

export type BarGraphValue = {
  key: string;
  label: string;
  value: number;
};

export type PublisherEntity = {
  gid: string;
  name: string;
  uri: string;
};

type PartnerId = {
  id: string;
  partner: string;
};

export type UserEntity = {
  country: string;
  employee: boolean;
  partnerUserIds: PartnerId[];
  permissions: string[];
  username: string;
  team: Team;
  email: string;
  fullName: string;
};

export type Team = {
  organizationUri: string;
  organizationName: string;
  resources: string[];
  permissions: string[];
};

export type WriterBasicInfo = {
  ipi?: string;
  name: string;
  isControlled?: boolean;
};

export type WriterResponse = {
  size: number;
  offset: number;
  numberOfWorks: number;
  writer: WriterEntity;
  works: WorkEntity[];
};

export type MatchSource = 'Manual add' | 'Spotify' | string;

export type RecordingEntity = {
  albumName: string;
  artistName: string;
  audioPreview: string;
  coverart300?: string;
  coverart640?: string;
  gid: string;
  isrc: string;
  popularity?: number;
  title: string;
  totalStreamCount: number;
  totalStreamCount28Days: number;
  totalStreamCountAllTime: number;
  releaseDate?: string;
  labelName?: string;
  licensorName?: string;
  matchSource?: MatchSource;
};

export type LatestReleaseRecording = RecordingEntity & {
  albumGid: string;
};

export type WorkEntity = {
  gid: string;
  iswc: string;
  recordings: Array<RecordingEntity>;
  numberOfRecordings: number;
  title: string;
  titleCased: string;
  countryStreamCounts: number;
  workNumber: string;
  writers: Array<WriterBasicInfo>;
};

export type WriterEntity = {
  name: string;
  ipi: string;
  topWork: {
    title: string;
    gid: string;
  };
  totalWorks: number;
  totalStreamCount: number;
  aliases?: string[];
  isControlled: boolean;
  songwriterPageStatus?: SongwriterPageStatusResponse;
};

export enum SongwriterPageStatus {
  Enabled = 'ENABLED',
  Disabled = 'DISABLED',
}

export type SongwriterPageStatusResponse = {
  creatorUri: string;
  songwriterPageStatus: SongwriterPageStatus;
};

export type TopWriters = {
  numberOfWriters: number;
  writers: WriterEntity[];
};

export type Playlist = {
  playlistId: string;
  title: string;
  dateAdded?: string;
  pictureUri?: string;
  totalStreamCount: number;
  type: 'Editorial' | 'Personalized' | 'Listener';
  recordings: Array<RecordingEntity>;
};

export type WrittenByPlaylist = {
  uri?: string;
  imageUrl?: string;
};

export type PublisherWriter = {
  publisherUri: string;
  writerIpi: string;
};

export type AllWrittenByPlaylistItem = {
  songwriterUri: string;
  creatorUri: string;
  formatListId: string;
  playlistUri: string;
  displayName: string;
  publisherWriters: PublisherWriter[];
};

export type AllWrittenByPlaylistResponse = AllWrittenByPlaylistItem[];

export type StreamsByProduct = {
  totalCount: number;
  percentages: {
    paid: number;
    free: number;
  };
};

export type RecordingSearchResult = {
  recording: RecordingEntity;
  workGid: string;
  workTitle: string;
};

export type TimeFilter = '7day' | '28day';

export type AnnotationType = 'PLAYLIST_ADD';

export type TimelineAnnotationsResponse = {
  annotations_by_date: {
    date: string;
    annotation_type_id: AnnotationType;
    annotation_metadata: {
      annotation_id: string;
      annotation_name: string;
    }[];
  }[];
};

export type TimelineAnnotationMetadata = {
  id: string;
  name: string;
};

export type TimelineAnnotation = {
  date: Date;
  type: AnnotationType;
  metadata: TimelineAnnotationMetadata[];
};

export type TeamMember = {
  userName: string;
  fullName?: string;
  email?: string;
  // TODO: convert to enum after auth migration
  groups: string[];
};

export type TeamResponse = {
  members: TeamMember[];
  totalMemberCount: number;
};

export type EligibilityStatus = 'ELIGIBLE' | 'NOT_ELIGIBLE' | 'ALREADY_MATCHED';

export type PotentialMatch = {
  recording: RecordingEntity;
  credits?: string[];
  eligibilityStatus: EligibilityStatus;
};

export type AccessibleRecording = {
  recording: RecordingEntity;
  accessibleByTeam: boolean;
  workGid: string;
  isHidden: boolean;
};

export type ManagingPublisher = {
  publisherUri: string;
  publisherName: string;
  writerIpis: string[];
};

export type SongwriterPageColorPalette = {
  textColorHex: string;
  bgColorHex: string;
};

// Array where the numbers are YEAR, MONTH, DAY
// This is the default serialization method from our Java date formatter.
export type ReleaseDate = number[];

export type Artist = {
  uri: string;
  name: string;
  coverArt?: string;
  coverArtLarge?: string;
};

export type LatestRelease = {
  title: string;
  artistNames: string[];
  coverArt?: string;
  coverArtLarge?: string;
  releaseDate: ReleaseDate;
  recordings: LatestReleaseRecording[];
};

export type CurationPagePermissions = {
  canPublishOrUnpublishPage: boolean;
};

export type SocialLink = {
  username: String | null;
  type: SocialLinkType;
};

export type SocialLinkType = 'tiktok' | 'instagram';

export type WriterCurationResponse = {
  colorPalette: SongwriterPageColorPalette;
  creatorUri: string;
  displayName: string;
  managingPublishers: ManagingPublisher[];
  permissions: CurationPagePermissions;
  recordings: AccessibleRecording[];
  songwriterImageUrl: string;
  songwriterPageStatus: SongwriterPageStatus;
  collaborators?: Artist[];
  latestRelease?: LatestRelease;
  socialLinks: SocialLink[];
};

export type WriterCatalogFilter = 'all' | 'controlled' | 'uncontrolled' | 'with-songwriter-page';

export type WriterCatalogParams = {
  paging: Paging;
  sorting: Sorting;
  filter: WriterCatalogFilter;
};
